import { SocketSessionError } from '@common/sockets/enums/socket-session-error.enum';
import { SocketEmitter } from '@common/sockets/socket-emitter';
import { EventEmitter } from '@common/util/event-emitter';
import { PropertyObservable } from '@common/util/property-observable.decorator';
import { SubscriptionProperty } from '@common/util/subscription-property.decorator';
import { URL } from '@common/util/url';
import { firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { io, ManagerOptions, Socket, SocketOptions } from 'socket.io-client';

export interface SessionOptions {
  socketBaseUrl: string;
  offline?: boolean;
  refreshToken$?: () => Observable<void>;
  /** Used in a NodeJS environment to pass auth config. Only used by collab tests at this time. */
  headers?: Dictionary<string>;
  /** Used in a NodeJS environment to support self-signed certs locally. Only used by collab tests at this time. */
  rejectUnauthorizedCerts?: boolean;
}

/**
 * SocketSession
 */
export abstract class SocketSession<TOptions extends SessionOptions, TError> extends EventEmitter {
  private socketBaseUrl: string;
  private refreshToken$: () => Observable<void>;
  private headers: Dictionary<string>;
  private rejectUnauthorizedCerts: boolean;

  protected emitter: SocketEmitter;
  @SubscriptionProperty() private offlineSubscription: Subscription;
  protected socket: Socket;

  /** Whether the initial connection has been established. Used to distinguish reconnects from the first connect. */
  private hasConnected: boolean = false;
  private reconnectedSubject: Subject<void> = new Subject<void>();
  /** Emits when the socket reconnects after having been connected before. Does not emit on the initial connection. */
  reconnected$: Observable<void> = this.reconnectedSubject.asObservable();

  connected: boolean = false;
  @PropertyObservable('connected') connected$: Observable<boolean>;

  stopped: boolean = false;
  @PropertyObservable('stopped') stopped$: Observable<boolean>;

  /** The session's current error if it is in an error state. */
  error: TError = null;
  /** An observable of the session's current error. */
  @PropertyObservable('error') error$: Observable<TError>;

  /**
   * Whether or not the session is offline. The session does not determine if it is offline or not.
   * This should be set whenever the client's offline state changes such as as browser's offline mode being toggled.
   */
  offline: boolean = false;
  @PropertyObservable('offline') offline$: Observable<boolean>;

  constructor(options: TOptions) {
    super();

    this.socketBaseUrl = options.socketBaseUrl;
    this.offline = options.offline ?? false;
    this.refreshToken$ = options.refreshToken$;
    this.headers = options.headers;
    this.rejectUnauthorizedCerts = options.rejectUnauthorizedCerts ?? true;

    // Listen to the offline status changing so that a new connection can be made when coming back online
    this.offlineSubscription = this.offline$.subscribe(offline => {
      if (!offline) {
        this.socket?.connect();
      }
    });
  }

  protected createSocketOptions(socketUrl: URL): Partial<ManagerOptions & SocketOptions> {
    return {
      extraHeaders: this.headers,
      path: socketUrl.pathname,
      rejectUnauthorized: this.rejectUnauthorizedCerts,
      withCredentials: true
    }
  }

  protected abstract parseError(event: string, error: any): TError;

  protected onStopped() { }

  /** Creates a session with the server by opening a socket connection and setting up event handlers. */
  public start() {
    // Initiate the socket connection
    const baseUrl = typeof document !== 'undefined' ? document.baseURI : undefined;
    const socketUrl = new URL(this.socketBaseUrl + '/socket.io', baseUrl);

    this.socket = io(`${socketUrl.protocol}//${socketUrl.host}`, this.createSocketOptions(socketUrl));

    this.socket.on('connect_error', async error => {
      await this.handleConnectError('connect_error', error);
    });

    this.socket.on('connect_timeout', error => {
      this.error = this.parseError('connect_timeout', error);
    });

    this.socket.on('reconnect_error', async error => {
      await this.handleConnectError('reconnect_error', error);
    });

    // Listen to disconnects in order to track the connection status
    this.socket.on('disconnect', reason => {
      this.connected = false;
      // Since the socket disconnected the emissions need to be canceled
      this.emitter?.cancelAllEmissions();
    });

    // Listen to connects in order to track the connection status
    this.socket.on('connect', () => {
      this.emitter.cancelAllEmissions();
      this.connected = true;
      this.error = null;
      if (this.hasConnected) {
        this.reconnectedSubject.next();
      }
      this.hasConnected = true;
    });

    // Create an emitter for sending messages over the socket
    this.emitter = new SocketEmitter(this.socket);
  }

  /** Disconnects the socket and cleans up resources. */
  public stop(): Promise<void> {
    return new Promise((resolve) => {
      this.socket?.once('disconnect', () => {
        this.emit('stopped');

        // Make sure all listeners are removed from the socket
        this.socket?.removeAllListeners();
        this.socket = null;

        // Remove all listeners from the session
        this.removeAllListeners();

        resolve();
      });

      this.stopped = true;
      this.socket?.disconnect();
      this.offlineSubscription = null;
      this.emitter?.cancelAllEmissions();
      this.emitter = null;

      this.onStopped();
    });
  }

  private async handleConnectError(event: string, error: any): Promise<void> {
    const isAccessDeniedError = error.data && error.data.name === SocketSessionError.AccessDenied;

    if (isAccessDeniedError && this.refreshToken$) {
      await firstValueFrom(this.refreshToken$());

      if (!this.stopped && !this.offline && !this.socket.connected) {
        this.socket.connect();
      }
    } else {
      this.error = this.parseError(event, error);
    }
  }
}
